/**
 * RowDetail (v3, HOF-467) — the expanded crop_cycle panel on the design-system
 * ``MarginBreakdown`` organism: the cost lines grouped by cost BEHAVIOUR (Schichten —
 * ertragsabhängig / ertragsunabhängig / Investition / Absatz) or, for an arable cycle, in the
 * KTBL order (Direktkosten → DkfL → Arbeitserledigung → DB1), read in ONE Bezugsgröße the
 * drawer switches on its own (€ gesamt / je ha / je kg|dt / je Reihenmeter / je Pflanze), with
 * the selected line's Rechenweg in the organism's permanently reserved side panel. Below it,
 * "Woher die Zahlen kommen" — the honesty notes and the provenance sentence — is unchanged.
 *
 * The invariant since #2302 holds: every subtotal the organism prints is the materialiser's
 * own ``dkfl`` / ``db1`` / ``db2`` (``subtotal.value``); the UI regroups, it never re-derives
 * a different DB1. The grouping lives in ``layers.ts``, the switcher in ``drawerBasis.ts``,
 * the Ackerbau discriminator in ``cropClass.ts``.
 *
 * #2442 — each source line is click-selectable (``?line=``); the selected line's SOURCE
 * BREAKDOWN — the individual inputs that sum to it, each deep-linking to its source in /farm
 * (and, for a price, the Preisspiegel), ending in a total that reconciles to the line or an
 * honest mismatch note — is what the Rechenweg panel shows. category_fallback lines have no
 * per-line source → a disabled row.
 *
 * #2508 — that breakdown is driven entirely by the builder's ``factors[]``/``items[]``
 * contract: ``Rechenweg`` (the derivation, rendered iff factors exist, as the panel's formula)
 * and the ``Belege`` lines (``Datum | Posten | Betrag``, links inside Posten, Betrag last).
 * This render path holds no crop- or line-specific string or branch, so new data in an
 * existing bucket appears with zero code change.
 */
import { useMemo, useState, type ReactNode } from 'react';
import {
  MarginBreakdown,
  type MarginBreakdownDetailData,
  type MarginBreakdownFlag,
  type MarginBreakdownLeistung,
} from '@hofsync/design-system';
import { useCropCycleOperations, type OperationInfo } from '../../../hooks/useDeckungsbeitrag';
import {
  geometryAssumptionSource,
  geometryAssumptionText,
  isGeometryBasis,
  type GeometrySource,
  type MarginBasis,
} from './basis';
import { isArableCrop } from './cropClass';
import { drawerBasisOptions, isDrawerBasis, type DrawerBasis } from './drawerBasis';
import {
  formatBookPrice,
  formatBookQty,
  formatEur,
  formatEurPerUnit,
  formatMonth,
  formatNumber,
  formatPostenQty,
  formatQty,
} from './format';
import {
  INVESTITION_LINE,
  LAYER_VIEW_OPTIONS,
  VERPACKUNG_LINE,
  layerBlocks,
  pendingLine,
  type CostLine,
  type LayerDecoration,
  type LayerView,
} from './layers';
import { perBasisEconomics } from './perUnit';
// #2773 C — the reason list. Same copy module the table chip and the summary note read.
import {
  BASIS_UNKNOWN_NOTE,
  CURATED_PRICE_NOTE,
  UnpricedChip,
  curatedPostenCount,
  unpricedNote,
} from './leistungMark';
import { classifyRow } from './rowQuality';
import { chipsFor } from './rowQualityCopy';
import {
  buildSourceBreakdown,
  bucketZeroReason,
  RECONCILE_TOLERANCE,
  canonicalLineKey,
  formatSharePercent,
  isMissingInputLine,
  lineBuckets,
  lineSelectability,
  operationsAttributable,
  shareOf,
  standProvenanceNote,
  standReasonText,
  zeroStateStand,
  type BreakdownFactor,
  type BreakdownItem,
  type BreakdownLink,
  type BreakdownResult,
  type ZeroReason,
} from './sourceBreakdown';
import { MODEL_SCOPE_NOTE, describesTheModel, lineBasisFor } from './lineBasis';
import type {
  DeckungsbeitragRow,
  InvestitionBlock,
  UnvaluedReason,
  LabourUnbooked,
  LabourUpliftGap,
  LeistungEstimate,
  ProductInconsistency,
  ProductInconsistencyReason,
  VerpackungMaterial,
} from './types';
import './RowDetail.css';

// ── #2695 E: a modelled Leistung is never money received ────────────────────────
/**
 * The estimate exists because a harvested arable cycle reported 0,00 € Leistung and a
 * confidently negative DB1. Valuing it from the curated sale price book fixes the sign — and
 * creates the opposite hazard in the same stroke: a five-figure € on the revenue line that no
 * buyer has paid, sitting one row away from cycles whose Leistung IS booked revenue. Every
 * surface below therefore names it, because the reader cannot tell the two apart from the €.
 */
const LEISTUNG_ESTIMATE_LABEL = 'Leistung (geschätzt)';

/** The Leistung row's hover — the short form of the note, for a reader who never opens the panel. */
const LEISTUNG_ESTIMATE_HOVER =
  'Kein Erlös gebucht. Diese Leistung ist aus der erfassten Erntemenge und einem Preis aus dem ' +
  'kuratierten Preisbuch berechnet — kein vereinnahmtes Geld.';

const LEISTUNG_ESTIMATE_HEADING = 'Leistung ist eine Schätzung';

/**
 * The full claim, in "Woher die Zahlen kommen": WHY there is a number (no revenue booked),
 * WHERE it comes from (the price book), WHAT it is not (money received), and what replaces it.
 * The last sentence matters as much as the warning — it tells the farmer the estimate is
 * temporary and self-correcting, so the honest label does not read as a defect.
 */
const LEISTUNG_ESTIMATE_BODY =
  'Für diese Kultur ist kein Erlös gebucht. Die Leistung ist aus der erfassten Erntemenge und ' +
  'einem Preis aus dem kuratierten Preisbuch berechnet — kein vereinnahmtes Geld. Sobald eine ' +
  'Gutschrift eingeht, ersetzt der gebuchte Erlös die Schätzung.';

/** #2168 — costs booked, revenue not ingested yet: the yield line says so, neutrally. */
const REVENUE_PENDING_NOTE = 'Für diesen Schlag sind noch keine Erlöse gebucht.';

/** #2442 — the affordance hint the organism shows while no line is selected. */
const SELECT_HINT = 'Tipp: Zeile antippen, um zu sehen, woraus sie sich zusammensetzt.';

/**
 * #2574 — what a DISABLED (source-less) line says on hover. The old copy claimed no bookings
 * EXIST; since #2536 a fallback row routinely serves real (unpriced) records, so the tooltip
 * may only state the provenance: no source lines to open. Branched per line, because a
 * disabled Gemeinkosten line is a DIFFERENT story (its € came from the #1711 overhead split,
 * and it is disabled only because the fact carries no ``allocations`` audit array), and
 * #2695 E adds a THIRD: an estimated Leistung whose operand block the mapper rejected — both
 * other sentences would be false about it, and dropping its warning is the one thing that
 * must not happen on any degrade.
 */
function disabledTitle(row: DeckungsbeitragRow, key: string): string {
  if (key === 'leistung' && row.leistung_estimated) {
    return `${LEISTUNG_ESTIMATE_HOVER} Der Rechenweg lässt sich für diesen Fakt nicht darstellen.`;
  }
  if (key === 'gemeinkosten') {
    return 'Für diesen Anteil liegt keine Aufteilung auf dem Fakt — es gibt keine Quellzeilen zum Aufklappen.';
  }
  // HOF-518 — the shared tooltip below is simply false about Landkosten: the charge is an
  // imputed €/ha rate off dem Preisspiegel, so it comes from no booking and no category sum.
  // A farmer told „stammt aus Kategorien-Summen" would go looking for bookings that by
  // construction do not exist. Reached when the fact books a land cost whose operand block
  // this app refused — the line keeps its €, loses only its Rechenweg.
  // Scoped to the IMPUTED line, exactly as its selectability branch is (codex r3): a BOOKED
  // Pacht (`exp_lease` → the land bucket) DOES have Einzelbuchungen, so this copy would be
  // backwards for it, and the shared tooltip below is the honest one on a fallback row.
  if (key === 'land' && (row.land_estimated || row.land_estimate)) {
    return 'Diese Zeile rechnet mit einem Pachtansatz je Hektar aus dem Preisspiegel — es gibt keine Einzelbuchungen dazu. Der Rechenweg lässt sich für diesen Fakt nicht darstellen.';
  }
  // Layer C — the same objection as Landkosten: the € is a template rate or a farm pool split
  // by area, so it came from no category sum. Reached when the fact books Investition € and
  // this app refused its block.
  if (key === INVESTITION_LINE) {
    return 'Diese Zeile rechnet mit Richtwerten je Anlagenklasse und verteilten Betriebssummen — es gibt keine Einzelbuchungen dazu. Der Rechenweg lässt sich für diesen Fakt nicht darstellen.';
  }
  return 'Dieser Betrag stammt aus Kategorien-Summen, nicht aus Einzelbuchungen — es gibt keine Quellzeilen zum Aufklappen.';
}

/**
 * #2844 — the honesty line under a geometry Bezugsgröße figure, rendered inside the panel
 * it qualifies: a per-plant figure is only as good as the density it came from, and it is
 * comparable only within one Kultur (``BASIS_META.crossCrop === false``).
 *
 * The within-crop caveat is a property of the BASIS and is therefore unconditional — a fact
 * that carries a ``plant_count`` but no ``geometry_provenance`` (every pre-#2303 fact) would
 * otherwise present €/Pflanze figures with no caveat at all, which is the inverse of what
 * this line exists for. The provenance sentence is prepended when the fact carries one, and
 * a DERIVED count is marked distinctly from a recorded one (glyph + colour): "18.000
 * Pflanzen" carries very different weight when it is 7 Pflanzen/m² × Fläche than when
 * somebody counted them.
 */
function GeometryAssumption({ text, source }: { text: string | null; source: GeometrySource | null }) {
  return (
    <p
      className={`deckungsbeitrag-detail__assumption${
        source ? ` deckungsbeitrag-detail__assumption--${source}` : ''
      }`}
    >
      {source && (
        <>
          <span className="deckungsbeitrag-detail__assumption-badge" aria-hidden="true">
            {source === 'derived' ? '≈' : '✓'}
          </span>{' '}
        </>
      )}
      {text && `${text} `}
      Nur innerhalb derselben Kultur vergleichbar.
    </p>
  );
}

const shortDay = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
};

function HarvestLine({ operations }: { operations: OperationInfo[] }) {
  const picks = operations
    .filter((op) => op.type === 'pick' || op.type === 'harvest')
    .filter((op) => op.date != null)
    .sort((a, b) => (a.date as string).localeCompare(b.date as string));
  if (picks.length === 0) return null;
  const first = picks[0].date as string;
  const last = picks[picks.length - 1].date as string;
  // HOF-104 — the „· Vermarktung: …" clause is gone with ``OperationInfo.channel``. It
  // listed the wiberry allocator's dominant sales channel per pick (`wishop:1731051`),
  // the label beside the retired kg-weighted booth share; the served payload no longer
  // carries it. The harvest window itself — how many Pflückungen and when — is
  // unaffected, and no replacement copy is added (HOF-104 scope cut).
  return (
    <p className="deckungsbeitrag-detail__harvest">
      Ernte: {picks.length} Pflückung{picks.length === 1 ? '' : 'en'}
      {first === last ? <> am {shortDay(first)}</> : <> zwischen {shortDay(first)} und {shortDay(last)}</>}
    </p>
  );
}

/** A cross-app deep-link. Its text comes from the builder and names the DESTINATION
 *  (a Kultur, a price, a Buchung — #2508), never the destination's category. Renders as a
 *  real anchor (href assertable / right-clickable); ``onNavigate`` (when provided) upgrades
 *  the click to in-app SPA navigation — absent, the anchor navigates normally. */
function DeepLink({
  link,
  onNavigate,
  children = null,
}: {
  link: BreakdownLink;
  onNavigate?: (to: string) => void;
  /** Optional since HOF-21 — a ``bare`` link renders the arrow alone. */
  children?: ReactNode;
}) {
  return (
    <a
      className="deckungsbeitrag-breakdown__link"
      href={link.to}
      // HOF-21 AC 18 — the accessible name comes from ``link.label``, ALWAYS, whether or
      // not that label is also on screen. It used to come from the rendered text alone,
      // with the arrow ``aria-hidden``; a bare link would then have had NO accessible name
      // and would announce as "link" and nothing else. Setting it unconditionally is also
      // what keeps the two cases from drifting: a link whose visible word and whose
      // announced word could differ is a link nobody would notice going wrong.
      aria-label={link.label}
      title={link.label}
      onClick={(e) => {
        if (onNavigate) {
          e.preventDefault();
          onNavigate(link.to);
        }
      }}
    >
      {children}
      {children != null && children !== '' ? ' ' : null}
      <span aria-hidden="true">↗</span>
    </a>
  );
}

/**
 * #2508 — join a cell's inline pieces (value / links / detail) with a " · " separator, so
 * the Posten and Rechenweg cells compose from whatever the data carries without either
 * one branching on WHICH pieces are present.
 */
function joinParts(parts: ReactNode[]): ReactNode[] {
  return parts.flatMap((node, i) =>
    i === 0
      ? [node]
      : [
          <span key={`sep-${i}`} className="deckungsbeitrag-breakdown__sep">
            {' · '}
          </span>,
          node,
        ],
  );
}

/** #2508 — an anchor per link, each labelled with its own destination. */
function LinkParts({ links, onNavigate }: { links: BreakdownLink[]; onNavigate?: (to: string) => void }) {
  return (
    <>
      {links.map((link) => (
        <DeepLink key={`${link.to}|${link.label}`} link={link} onNavigate={onNavigate}>
          {/* HOF-21 AC 18 — a ``bare`` link renders the arrow alone. The label is not lost:
              ``DeepLink`` carries it into ``aria-label``/``title``, which is the whole
              reason this is a flag and not an empty ``label``. */}
          {link.bare ? null : link.label}
        </DeepLink>
      ))}
    </>
  );
}

/**
 * #2508 — one ``Rechenweg`` factor's value cell: the pre-formatted value (or, when the
 * value IS the link, the anchor alone), plus the ``geschätzt`` badge on an estimated
 * factor. A non-estimated factor's ``note`` becomes the value's hover explanation.
 */
function FactorValue({ factor, onNavigate }: { factor: BreakdownFactor; onNavigate?: (to: string) => void }) {
  const parts: ReactNode[] = [];
  if (factor.value) {
    parts.push(
      <span key="value" title={factor.note ?? undefined}>
        {factor.value}
      </span>,
    );
  }
  for (const link of factor.links) {
    parts.push(
      <DeepLink key={`${link.to}|${link.label}`} link={link} onNavigate={onNavigate}>
        {link.label}
      </DeepLink>,
    );
  }
  // 2026-11-03 (Antonio) — no „geschätzt" badge in the detail rows; the note is the hover.
  return <>{joinParts(parts)}</>;
}

/**
 * #2476 — the German copy for a 0/blank cascade line's reason token. ``missing_price``
 * explains "input present, price unknown" (the harvested Menge for Leistung, else the
 * producing-record count); ``no_records`` is the flat "nothing recorded" token. The
 * Leistung picks-but-0-kg case reads its named phrase, per the issue.
 */
function reasonTokenText(lineKey: string, reason: ZeroReason): string {
  // #2593 — this 0,00 € is a RULE OUTCOME, not a data gap: the stand is written off or not
  // yet planted. Rendered from the same helper the Rechenweg factor uses, so the line and the
  // opened drawer can never tell the reader two different stories.
  if (reason.state === 'stand_outside_window' && reason.stand) return standReasonText(reason.stand);
  if (reason.state === 'no_records') return 'Keine Erfassung';
  if (lineKey === 'leistung') {
    const qty = reason.harvestQty ?? 0;
    const unit = reason.harvestUnit ?? null;
    // #2695 E RE-AIMS the 0-kg half of this token. Until the sale price book existed, "kein
    // Preis" was the whole story for both branches. It no longer is: the book may well price
    // this crop, and the fallback still cannot help, because it would multiply that price by
    // zero. Naming the price as the blocker now sends the reader off to fix the one thing that
    // is not broken, so the copy names the MENGE — which stays true whether or not a price
    // exists, and the fact carries no "the book prices this crop" signal for the copy to lean
    // on. The issue's scope-limit #1 asked for this decision explicitly: the 0-kg case stays
    // honest at 0 (no norm-yield second estimate) and says why.
    //
    // The copy also may not PROMISE that a Menge would unlock an estimate: the book prices
    // arable crops only, and the shape this branch fires on is typically a berry/Kirschen
    // cycle, which the book deliberately excludes. It states what is missing, nothing more.
    return qty > 0
      ? `${formatQty(qty, unit)} erfasst, aber kein Preis`
      : `Ernte erfasst, aber ${formatQty(0, unit)} — ohne erfasste Menge nicht berechenbar`;
  }
  const n = reason.recordCount;
  return `Preis fehlt · ${n} Buchung${n === 1 ? '' : 'en'}`;
}

/**
 * #2510/#2508 — the ``Ø`` in a Posten's ``Menge × Ø-Preis`` strip. The price is DERIVED
 * (Betrag ÷ Menge), so it is an average over whatever that one booking contains and the
 * displayed rounding need not re-multiply to the exact cent. #2510 defers the wording to
 * this issue: rather than dropping the marker (the FE cannot tell a single-price booking
 * from a blended one), the marker stays and now says what it means on hover — which holds
 * just as well once #2510 makes a booking's revenue an allocated share.
 */
const AVG_UNIT_PRICE_NOTE =
  'Durchschnittspreis dieser Buchung: Betrag ÷ Menge — die Anzeige-Rundung kann minimal abweichen.';

/**
 * #2460/#2508 — the Posten cell, the ONLY identity column: the ``Menge × Ø-Preis`` strip
 * when the item carries a quantity, else its primary ``label``; then its deep-link(s)
 * (each naming its own destination — nothing sits behind the Betrag column any more); then
 * the secondary ``detail`` (channel / Löhne-Basis / Maschinen-Namen / Methode / Menge).
 * Every piece comes from the builder, so this holds no crop- or line-specific branch.
 *
 * HOF-467 follow-up — a Beleg that both NAMES something (kind · Artikel) and carries a Menge
 * reads on two lines: the identity, then the ``Menge × Ø`` strip as a block of its own. No
 * separator sits between the two — inline, the „·" dangled at the end of the first line
 * whenever the panel wrapped the strip. Any other cell keeps the single joined line.
 */
function PostenCell({ item, onNavigate }: { item: BreakdownItem; onNavigate?: (to: string) => void }) {
  const { qty, unit, unitPrice } = item;
  const identity: ReactNode[] = [];
  const rest: ReactNode[] = [];
  const hasStrip = qty != null && qty > 0;
  // HOF-467 follow-up — the KIND leads (a material's Kategorie), then the identity, then the
  // Menge × Ø strip: „Insektizide · SpinTor · 0,08 l ↗ × Ø 385,00 €/l ↗". A row whose identity
  // is its link keeps ``label: ''`` and starts with the strip, exactly as before.
  if (item.kind) {
    identity.push(
      <span key="kind" className="deckungsbeitrag-breakdown__kind">
        {item.kind}
      </span>,
    );
  }
  if (item.label) identity.push(<span key="label">{item.label}</span>);
  // A MONTH row (``item.month``) whose picks carried no Menge has no strip to render, yet the
  // month's Ø and its Preisspiegel arrow are what the row is about — so they render after the
  // count that stands as its label rather than vanishing with the strip. A per-booking row
  // never reaches this branch with a price: the builder pairs a price with a Menge or neither.
  if (!hasStrip && unitPrice) {
    rest.push(
      <span
        key="price"
        className="deckungsbeitrag-breakdown__qtyprice"
        title={item.postenNote ?? AVG_UNIT_PRICE_NOTE}
      >
        × Ø {formatBookPrice(unitPrice.value, unitPrice.unit)}
        {item.priceLink && (
          <>
            {' '}
            <DeepLink link={item.priceLink} onNavigate={onNavigate} />
          </>
        )}
      </span>,
    );
  }
  if (hasStrip) {
    // ``unit`` may be null (pre-#2168 pick rows); formatQty/formatEurPerUnit default null → kg.
    rest.push(
      <span
        key="qtyprice"
        className="deckungsbeitrag-breakdown__qtyprice"
        title={item.postenNote ?? AVG_UNIT_PRICE_NOTE}
      >
        {/* HOF-102 — the Ø comes OFF THE FACT when the Posten has one, with its own unit.
            ``|amount| ÷ qty`` is only the price when both operands share a unit, and a
            €/dt book price against a Menge in kg is a routine shape — the quotient would
            print a tenth of the price under a „€/kg" label. ``formatBookPrice`` (not
            ``formatEurPerUnit``) for exactly that reason: it prints the fact's unit
            verbatim rather than relabelling it from the quantity's. */}
        {/* An UNPRICED Posten shows its Menge and stops. There is no Ø behind it, and the
            derived quotient would be `0 ÷ Menge` — „Ø 0,00 €/kg" beside a „nicht bewertet"
            chip, which reads as *sold for nothing* rather than *not priced*. The chip and
            its reason carry the rest; a 0 that looks like a price is the one thing D2
            cannot afford to print. */}
        {/* HOF-467 follow-up — the Menge in the booking's OWN unit: a litre of Pflanzenschutz is
            not a kilogram. ``formatPostenQty`` normalises the yield units exactly as ``formatQty``
            did and keeps every other declared unit verbatim; only an absent unit still reads kg. */}
        {formatPostenQty(qty, unit)}
        {/* HOF-143 — the quantity's own source, INSIDE the strip and spaced, on exactly the
            reasoning HOF-21 AC 19 used for the price link one line below: it addresses the
            MENGE and not the row, so it belongs against the kg rather than a column away at
            the end of the cell. Which link this is comes off the builder — the renderer
            places what it is handed and reads nothing about the destination. */}
        {item.qtyLink && (
          <>
            {' '}
            <DeepLink link={item.qtyLink} onNavigate={onNavigate} />
          </>
        )}
        {/* The Ø is shown ONLY when the fact carries one, and there is no derived
            fallback left on this line. Two ways to reach a priced Posten with no
            ``unit_price``/``price_unit`` pair — the mapper drops a price whose unit is
            missing, and a Posten can carry a price the worker chose not to publish — and
            in both `|Betrag| ÷ Menge` would be labelled with the QUANTITY's unit. That is
            the €/dt-as-€/kg mislabelling this change exists to end, so the fallback would
            reintroduce it on precisely the rows where the fact declined to state the unit.
            The Menge alone is the weaker, true answer. Found by codex. */}
        {unitPrice && (
          <>
            {' '}
            × Ø {formatBookPrice(unitPrice.value, unitPrice.unit)}
          </>
        )}
        {/* HOF-21 AC 19 — INSIDE the strip, right after the price, because it addresses the
            PRICE and not the row: it opens the Preisspiegel on the item and the month this
            Posten's € actually came from. Rendering it beside the row's other links would
            put it a column away from the number it explains.
            HOF-143 — SPACED off the price. It rendered glued („5,31 €/kg↗"), which reads as
            part of the unit rather than as a link, and left the pair of arrows in the cell
            looking like one broken one. */}
        {item.priceLink && (
          <>
            {' '}
            <DeepLink link={item.priceLink} onNavigate={onNavigate} />
          </>
        )}
      </span>,
    );
  }
  if (item.links.length > 0) {
    rest.push(<LinkParts key="links" links={item.links} onNavigate={onNavigate} />);
  }
  if (item.detail) {
    rest.push(
      // 2026-11-03 (Antonio) — no „geschätzt" badge on a detail row: it confused more than it
      // explained. An allocation's reason (#2769-a) stays as the hover of its detail, and the
      // form's „Woher die Zahlen kommen" says how a share is computed.
      <span key="detail" className="deckungsbeitrag-breakdown__detail" title={item.note ?? undefined}>
        {item.detail}
      </span>,
    );
  }
  const badges = (
    <>
      {/* HOF-102 — marked only when there is nothing behind the Menge. Every other Posten
          on this line IS a Preisspiegel valuation, so a chip saying so on each of them
          would be the wallpaper a reader stops seeing; the exception is what has to be
          visible, and the reason rides in its hover. */}
      {item.unpriced && <UnpricedChip title={item.postenNote ?? undefined} />}
    </>
  );
  if (identity.length > 0 && hasStrip) {
    // Two lines. The badges ride on the strip's line: they qualify the Menge and its price.
    return (
      <>
        <span className="deckungsbeitrag-breakdown__ident">{joinParts(identity)}</span>
        <span className="deckungsbeitrag-breakdown__strip">
          {joinParts(rest)}
          {badges}
        </span>
      </>
    );
  }
  return (
    <>
      {joinParts([...identity, ...rest])}
      {badges}
    </>
  );
}

/**
 * #2487 Part C — the German copy for each field-vs-revenue divergence class. ``crop`` fills
 * the mismatch phrase with the resolved crop when known. Part E adds the Sorte class.
 */
function inconsistencyReasonText(reason: ProductInconsistencyReason, fieldCrop: string | null): string {
  switch (reason) {
    case 'crop_mismatch':
      return fieldCrop
        ? `Umsatz stammt aus einer anderen Kultur als ${fieldCrop}.`
        : 'Umsatz stammt aus einer anderen Kultur.';
    case 'resale_mixed':
      return 'Zugekaufte Handelsware auf diese Kultur gebucht.';
    case 'revenue_without_yield':
      return 'Umsatz ohne passende Ernte gebucht — wahrscheinlich zugekauft.';
    case 'variety_mismatch':
      return 'Zwei Quellen melden unterschiedliche Sorten für diese Anlage — bitte prüfen, welche stimmt.';
  }
}

/**
 * #2487 Part E — a pure Sorte disagreement is not about revenue, so the note's heading adapts.
 * Any signal that ALSO carries a revenue-side reason keeps the original Part-C heading, so a
 * pre-Part-E signal renders byte-identically.
 */
function inconsistencyHeading(signal: ProductInconsistency): string {
  const varietyOnly = signal.reasons.length === 1 && signal.reasons[0] === 'variety_mismatch';
  return varietyOnly ? 'Sorte weicht zwischen Quellen ab' : 'Umsatz passt nicht zur Kultur';
}

/**
 * #2487 Part C — the honesty note surfaced when the grown crop and the product(s) behind the
 * revenue diverge. Generalises the resale/Handelsware warning into one explainable block, so
 * a farmer sees WHY a margin looks off instead of a silently re-booked or under-counted number.
 * Part E folds in the Sorte axis: where an external variety source (FruitForecast) disagrees
 * with the twin's Sorte, both readings are NAMED — the app never silently picks a winner.
 */
/** #4172 — the heading of the „labour that reached no line" disclosure. */
const LABOUR_UNBOOKED_HEADING = 'Lohn ohne Kostenart';

function ProductInconsistencyNote({ signal }: { signal: ProductInconsistency }) {
  const products = signal.revenue_products.filter((p) => p.product);
  const heading = inconsistencyHeading(signal);
  const varieties = signal.varieties;
  return (
    <div className="deckungsbeitrag-detail__inconsistency" role="note" aria-label={heading}>
      <p className="deckungsbeitrag-detail__inconsistency-head">
        <span className="deckungsbeitrag-detail__inconsistency-badge" aria-hidden="true">
          !
        </span>{' '}
        {heading}
      </p>
      <ul className="deckungsbeitrag-detail__inconsistency-reasons">
        {signal.reasons.map((r) => (
          <li key={r}>{inconsistencyReasonText(r, signal.field_crop)}</li>
        ))}
      </ul>
      {varieties && (
        <p className="deckungsbeitrag-detail__inconsistency-varieties">
          Sorte hier: {varieties.field_variety ?? 'nicht erfasst'} · laut{' '}
          {varieties.external_source ?? 'Fremdsystem'}: {varieties.external_variety ?? 'nicht erfasst'}
        </p>
      )}
      {products.length > 0 && (
        <p className="deckungsbeitrag-detail__inconsistency-products">
          Betroffene Produkte:{' '}
          {products.map((p, i) => (
            <span key={`${p.product}-${i}`}>
              {i > 0 ? ', ' : ''}
              {p.product}
              {p.crop ? ` (${p.crop})` : ''}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/**
 * #2695 E — the honesty note for a modelled Leistung, at the top of "Woher die Zahlen kommen".
 *
 * Placed FIRST in that column deliberately: it is a claim about what the headline € is, and it
 * has to land before the reader reaches the provenance sentence underneath (which speaks about
 * cost bookings and would otherwise be read as covering the revenue line too).
 *
 * The operand line renders only when the fact carries a usable block. A flagged row whose block
 * did not map keeps the warning and loses the arithmetic — the right way round, because
 * under-labelling is the failure this whole subtask exists to prevent.
 */
/** The drawer heading for the #2773 C reason list. */
/**
 * HOF-512 — „dieser Deckungsbeitrag", not „diese Zahl".
 *
 * The fourth instance of this change's characteristic failure: a row-level sentence that
 * read as obvious under the cost table and reads as line-level inside a per-line panel.
 * „Warum diese Zahl nicht in der Übersicht steht" over an open „Aufschlüsselung:
 * Saatgut/Pflanzen" points „diese Zahl" straight at the Saatgut number, while the chips
 * below it judge the row's DB1 and „die Übersicht" ranks rows rather than lines — the
 * bullets already say „Der Deckungsbeitrag …", so only the heading was ambiguous.
 */
const ROW_QUALITY_HEADING = 'Warum dieser Deckungsbeitrag nicht in der Übersicht steht';

/**
 * #2773 C — the reasons the row classifier fired, spelled out.
 *
 * The table chip names the class in two words; this is where the reader learns what to
 * actually DO about it. Renders nothing for an ``ok`` row (an empty honesty box trains
 * people to ignore the full one) and nothing for ``leistung_modelled``, which has its own
 * richer block directly above — ``chipsFor`` already drops that token, so the two notes
 * cannot both claim the same fact.
 */
function RowQualityNote({ row }: { row: DeckungsbeitragRow }) {
  // ``chipsFor`` gates on the STATE, not just the reasons: an ``ok`` row can still
  // carry marker-style trust reasons (an all-zero cycle with hollow buckets stays
  // visible by design), and this heading would then claim the row is missing from an
  // overview it is actually in.
  const chips = chipsFor(classifyRow(row), 'schlag');
  if (chips.length === 0) return null;
  return (
    <div className="deckungsbeitrag-detail__quality" role="note" aria-label={ROW_QUALITY_HEADING}>
      <p className="deckungsbeitrag-detail__quality-head">{ROW_QUALITY_HEADING}</p>
      <ul className="deckungsbeitrag-detail__quality-list">
        {chips.map((chip) => (
          <li key={chip.reason}>
            <strong>{chip.label}</strong> — {chip.title}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * #4172 wave 2 — labour money that reached no cost line, named where it can be fixed.
 *
 * It rides on the FACT rather than in a log because the person who can fix it — someone
 * adding a missing label to the activity-classes document, or a month's pick record to the
 * Schlagkartei — reads this app, not ELK. And it is stated rather than smeared: a euro with
 * no activity class is not evidence about any one crop, so allocating it would put a number
 * on a line that has not earned one.
 *
 * Renders nothing on the overwhelming majority of facts, where every euro was booked.
 */
function LabourUnbookedNote({ unbooked }: { unbooked: LabourUnbooked | null }) {
  if (!unbooked) return null;
  const unclassified = unbooked.eur > 0;
  const unallocated = (unbooked.packing_unallocated ?? 0) > 0;
  if (!unclassified && !unallocated) return null;
  return (
    <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={LABOUR_UNBOOKED_HEADING}>
      <p className="deckungsbeitrag-detail__estimate-head">{LABOUR_UNBOOKED_HEADING}</p>
      {unclassified && (
        <p>
          {formatEur(unbooked.eur)}
          {unbooked.hours > 0 ? ` (${formatNumber(unbooked.hours)} Std.)` : ''} aus der Lohnabrechnung tragen keine
          Tätigkeit, die sich einer Kostenart zuordnen lässt — sie stehen in keiner Zeile oben. Fehlt die Tätigkeit im
          Katalog, taucht der Betrag auf, sobald sie ergänzt ist.
        </p>
      )}
      {unallocated && (
        <p>
          In der Packhalle ließen sich {formatEur(unbooked.packing_unallocated!)} nicht verteilen: in dem Monat, in dem
          sie angefallen sind, ist für keine der betroffenen Kulturen eine Pflückmenge erfasst, an der sie hängen
          könnten. Der Betrag wird ausgewiesen statt gleichmäßig verschmiert zu werden — es ist die Summe der{' '}
          <strong>ganzen Halle</strong>, nicht der Anteil dieses Anbaus, und sie steht deshalb bei jedem betroffenen
          Anbau gleich da.
        </p>
      )}
    </div>
  );
}

/** The heading of the „wage booked without its employer cost" disclosure (HOF-644). */
const LABOUR_UPLIFT_GAP_HEADING = 'Lohnnebenkosten unvollständig';

/**
 * Wage the payroll book should have priced and could not: the month is covered, but the
 * person found no Personengruppe or no rate. That wage is booked at what was paid, without
 * the employer's share, so the labour lines above read LOW by it. Said with the amount,
 * because the fix is a payroll question the farm can answer (which Personengruppe).
 *
 * Renders nothing when every wage found its rate, which is the ordinary case.
 */
function LabourUpliftGapNote({ gap }: { gap: LabourUpliftGap | null }) {
  if (!gap) return null;
  return (
    <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={LABOUR_UPLIFT_GAP_HEADING}>
      <p className="deckungsbeitrag-detail__estimate-head">{LABOUR_UPLIFT_GAP_HEADING}</p>
      {gap.wage_eur > 0 && (
        <p>
          {formatEur(gap.wage_eur)} Lohn aus {formatNumber(gap.records)} Lohnbuchungen
          {gap.hours > 0 ? ` (${formatNumber(gap.hours)} Std.)` : ''} stehen ohne Arbeitgeberanteil in den Zeilen
          oben: Für die Person fehlt in dem Monat eine Personengruppe oder ein Satz aus der Lohnabrechnung. Die
          Lohnzeilen sind um diesen Anteil zu niedrig.
        </p>
      )}
      {gap.pooled_wage_eur > 0 && (
        <p>
          Dazu kommen {formatEur(gap.pooled_wage_eur)} Lohn aus Sammelposten (Packhalle, Fahrten und Ähnliches), die
          diesem Anbau ohne Arbeitgeberanteil zugeteilt sind.
        </p>
      )}
    </div>
  );
}

/** The heading of the share explanation (2026-11-03). */
const LABOUR_SHARE_HEADING = 'Wie Umlage und Fester Kulturanteil gerechnet sind';

const SHARE_LABEL: Record<'pool_share' | 'fixed_share', string> = {
  pool_share: 'Umlage Arbeit ohne Feld',
  fixed_share: 'Fester Kulturanteil',
};

/**
 * How the two shared labour Posten of the open line are computed, with each month's
 * „Topf × Anteil = Betrag" — so the bookkeeper can follow a share to its euro on the form
 * itself, not only in a hover (2026-11-03). Renders nothing on a line without such a row.
 */
function LabourShareNote({ row, lineKey }: { row: DeckungsbeitragRow; lineKey: string | null }) {
  if (!lineKey) return null;
  const items = lineBuckets(lineKey)
    .flatMap((b) => row.labour_posten?.[b as keyof NonNullable<typeof row.labour_posten>] ?? [])
    .filter(
      (i): i is typeof i & { kind: 'pool_share' | 'fixed_share' } =>
        (i.kind === 'pool_share' || i.kind === 'fixed_share') && i.pool != null && i.pool > 0,
    )
    .sort((a, b) => (a.month ?? '').localeCompare(b.month ?? '') || a.kind.localeCompare(b.kind));
  if (items.length === 0) return null;
  const kinds = new Set(items.map((i) => i.kind));
  return (
    <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={LABOUR_SHARE_HEADING}>
      <p className="deckungsbeitrag-detail__estimate-head">{LABOUR_SHARE_HEADING}</p>
      {kinds.has('pool_share') && (
        <p>
          Umlage Arbeit ohne Feld: Lohn, der in der Zeiterfassung keinem Feld zugeordnet ist (etwa Fahren, Küche,
          Kontrolle Ernte, Wiegen, Teamleitung). Die Lohnsumme eines Monats wird nach den in diesem Monat gepflückten
          Kilogramm verteilt — je nach Tätigkeit auf alle Kulturen oder nur auf die eigene Kultur. Dieser Anbau trägt
          den Anteil seiner gepflückten Kilogramm.
        </p>
      )}
      {kinds.has('fixed_share') && (
        <p>
          Fester Kulturanteil: Festangestellte ohne Zeiterfassung. Ihre Arbeitgeberkosten laut Lohnabrechnung im
          Monat × der Anteil, den der Betrieb für sie je Kultur festgelegt hat, ergeben den Topf der Kultur. Er wird
          nach den im Monat gepflückten Kilogramm auf die Anbauten der Kultur verteilt. Ein Kulturanteil aus weniger
          als drei Personen wird nicht verteilt.
        </p>
      )}
      <ul className="deckungsbeitrag-detail__share-steps">
        {items.map((i) => (
          <li key={`${i.month}-${i.kind}`}>
            {i.month ? `${formatMonth(i.month)} · ` : ''}
            {SHARE_LABEL[i.kind]}: {formatEur(i.pool!)} × {formatSharePercent(shareOf(i))} = {formatEur(Math.abs(i.amount))}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The heading of the „packaging pieces no invoice priced" disclosure. */
const VERPACKUNG_UNPRICED_HEADING = 'Verpackung ohne Rechnungspreis';

/**
 * Packaging pieces the Verpackung line counted and could not price.
 *
 * The count is measured (off the delivery lines) and the price is not guessed, so each such
 * piece books 0,00 € and the line is a LOWER bound. That is worth saying in words, with the
 * count, because the fix is in the farmer's hands: the missing invoice — or one that states
 * pieces rather than cartons — prices the article the moment it is in the archive.
 *
 * Renders nothing when every piece had a price, which is the ordinary case.
 */
function VerpackungUnpricedNote({ material }: { material: VerpackungMaterial | null }) {
  if (!material || material.unpriced.length === 0) return null;
  return (
    <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={VERPACKUNG_UNPRICED_HEADING}>
      <p className="deckungsbeitrag-detail__estimate-head">{VERPACKUNG_UNPRICED_HEADING}</p>
      <p>
        {material.unpriced.map((u) => `${formatNumber(u.pieces)} × ${u.article_label ?? u.article}`).join(', ')}: für
        diese Artikel liegt keine Rechnung mit Stückpreis vor. Sie stehen mit 0,00 € in der Zeile Verpackung — die Zeile
        ist deshalb eine Untergrenze. Sobald die Rechnung im Archiv ist und Stückzahlen nennt, wird der Betrag
        nachgezogen.
      </p>
    </div>
  );
}

/** The heading of the registered tunnels that carry no Investition row (AC 21). */
const INVESTITION_UNVALUED_HEADING = 'Nicht verteilt';

/** The heading of the Investition line's „charged to no cycle" list. */
const INVESTITION_UNALLOCATED_HEADING = 'Nicht verteilt (nur zur Einordnung)';

const INVESTITION_UNALLOCATED_REASON: Record<string, string> = {
  not_split: 'noch keiner Anlage und Kultur zugeordnet',
  no_population: 'keine Kultur trägt diese Klasse',
};

/** Why a registered tunnel carries no Investition row. */
const UNVALUED_REASON_LABEL: Record<UnvaluedReason, string> = {
  no_cost: 'kein Anschaffungswert, nicht bewertet',
  invalid_operands: 'ungültige Angaben, nicht bewertet',
  no_charge: 'kein Nutzungsentgelt hinterlegt, nicht bewertet',
  leased_not_valued: 'geleast — noch nicht bewertet',
};

/**
 * Layer C — the farm pools the Investition line charged to NO cycle (an „AfA Sachanlagen"
 * of 420.000 € the BWA does not split by installation).
 *
 * Kept OUT of the Belege table on purpose: a row there is read as part of its Σ, and these
 * euros are in no Deckungsbeitrag at all. They are listed so the farmer sees what the farm
 * carries in total, and the copy says in so many words that they are not in this one.
 * Rendered only under the open Investition line — it is a claim about that line.
 */
function InvestitionUnallocatedNote({ block }: { block: InvestitionBlock | null }) {
  const suppressed = block?.suppressed_classes ?? [];
  const unvalued = block?.unvalued ?? [];
  if (!block || (block.unallocated.length === 0 && suppressed.length === 0 && unvalued.length === 0)) return null;
  return (
    <>
      {suppressed.map((s) => (
        <div key={s.id} className="deckungsbeitrag-detail__estimate" role="note">
          <p>{`Richtwert „${s.label}" ersetzt durch die registrierten Tunnel dieses Schlags.`}</p>
        </div>
      ))}
      {unvalued.length > 0 && (
        <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={INVESTITION_UNVALUED_HEADING}>
          <p className="deckungsbeitrag-detail__estimate-head">{INVESTITION_UNVALUED_HEADING}</p>
          <ul>
            {unvalued.map((u) => (
              <li key={u.asset.entity_id}>
                {u.asset.name ?? u.asset.entity_id} — {UNVALUED_REASON_LABEL[u.reason]}
              </li>
            ))}
          </ul>
        </div>
      )}
      {block.unallocated.length > 0 && (
        <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={INVESTITION_UNALLOCATED_HEADING}>
          <p className="deckungsbeitrag-detail__estimate-head">{INVESTITION_UNALLOCATED_HEADING}</p>
          <p>
            Diese Beträge des Betriebs lassen sich noch keiner Kultur zuordnen. Sie sind nicht in diesem
            Deckungsbeitrag enthalten und stehen nur hier, damit sichtbar ist, was der Betrieb insgesamt trägt.
          </p>
          <ul>
            {block.unallocated.map((u) => (
              <li key={u.id || u.label}>
                {u.label}: {formatEur(u.total)} — {INVESTITION_UNALLOCATED_REASON[u.reason]}
                {u.source ? ` (Quelle: ${u.source})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function LeistungEstimateNote({ estimate }: { estimate: LeistungEstimate | null }) {
  return (
    <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={LEISTUNG_ESTIMATE_HEADING}>
      <p className="deckungsbeitrag-detail__estimate-head">
        <span className="deckungsbeitrag-detail__estimate-badge" aria-hidden="true">
          ~
        </span>{' '}
        {LEISTUNG_ESTIMATE_HEADING}
      </p>
      <p>{LEISTUNG_ESTIMATE_BODY}</p>
      {estimate && (
        <p className="deckungsbeitrag-detail__estimate-formula">
          {/* Both units come straight off the fact — see format.ts on why NOT formatQty/
              formatEurPerUnit, whose unitLabel would relabel a €/t book price as €/kg. */}
          {formatBookQty(estimate.quantity, estimate.quantity_unit)} ×{' '}
          {formatBookPrice(estimate.unit_price, estimate.price_unit)} = {formatEur(estimate.value)}
        </p>
      )}
    </div>
  );
}

/**
 * HOF-102 — what the Leistung IS, said once, where the cascade is.
 *
 * Deliberately a note and not a chip. The basis is true of every Leistung on this page —
 * a mark that never varies marks nothing — and the two caveats it carries are exactly the
 * kind a reader needs *while looking at the number*, not as a pill they have to hover:
 *
 *   - **D2, the unpriced Posten.** How many kilos went into the Σ at 0 €, and which
 *     direction that pushes the Deckungsbeitrag (down). The panel below says WHICH.
 *   - **D1, the VAT basis.** Until HOF-45 nets the sell side at mint time, a blend folded
 *     from cells with no declared basis may be gross. Bounded (~7–10 % on berries) — and
 *     the reason it is not a row-quality chip is that it fires on essentially every prod
 *     cycle today, so as a warning it would drown the one that varies.
 *
 * Renders nothing when the fact carries no Posten block: a row whose breakdown this app
 * could not read must not be told how its Leistung was computed, because it does not know.
 * HOF-518 adds the second such guard, for the same reason: the Landkosten basis names the
 * Preisspiegel rate the € was IMPUTED from, which is false of a booked Pacht.
 */
function LineBasisNote({ row, lineKey }: { row: DeckungsbeitragRow; lineKey: string | null }) {
  const entry = lineBasisFor(lineKey);
  if (!entry) return null;
  // The Leistung's basis was long the one sentence here that is NOT unconditionally true of
  // the line (HOF-518's Landkosten is the second — see the guard below it): a fact carrying no Posten block may be a ``leistung_estimated`` row, whose € came
  // from the curated book and not from „Menge × Monatsdurchschnitt" at all — the worker
  // emits the two as an either/or. ``LeistungEstimateNote`` above already says what such a
  // row is; adding this claim beside it would contradict it on the same screen. Every OTHER
  // cost line's basis describes the model rather than this fact and needs no guard — with the
  // one exception directly below, which HOF-518 introduced.
  if (lineKey === 'leistung' && row.leistung_posten === null) return null;
  // HOF-518 (codex r3) — that exception. „Fläche × Pachtansatz je Hektar aus dem Preisspiegel" describes
  // how THIS € was made, not how the model works in general, and it is simply false of a
  // BOOKED Pacht (`exp_lease` → the land bucket): that money came off a bank statement. The
  // farmer sees the booking rows instead, which is a better answer than a wrong sentence.
  if (lineKey === 'land' && !row.land_estimated && row.land_estimate === null) return null;
  // The Leistung's caveats are the Leistung's — they qualify THAT number and belong only
  // under it. Hanging them on Saatgut would be the defect this change exists to remove,
  // one level down: a true sentence about the wrong line is still a false claim about the
  // line the farmer opened.
  const leistung = lineKey === 'leistung' ? leistungCaveats(row) : [];
  return (
    <div className="deckungsbeitrag-detail__estimate" role="note" aria-label={entry.heading}>
      <p className="deckungsbeitrag-detail__estimate-head">{entry.heading}</p>
      {entry.body.map((paragraph: string) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
      {leistung.map((caveat: string) => (
        <p key={caveat}>{caveat}</p>
      ))}
      {describesTheModel(lineKey) && <p className="deckungsbeitrag-detail__model-scope">{MODEL_SCOPE_NOTE}</p>}
    </div>
  );
}

/**
 * The Leistung's own caveats, in the order a reader needs them: how much of the harvest
 * went in unpriced (D2), what was valued from the curated book rather than the farm's own
 * sales, and whether the € may still carry VAT (D1). Each is a claim about THIS cycle, so
 * each is conditional; the basis sentence above them is unconditional and lives in the KB.
 *
 * Returns nothing when the fact carries no Posten block: a row whose breakdown this app
 * could not read must not be told what its Leistung is made of, because it does not know.
 */
function leistungCaveats(row: DeckungsbeitragRow): string[] {
  const block = row.leistung_posten;
  if (block === null) return [];
  const out: string[] = [];
  // The count off the FACT's own flag, not the block: a fact can carry the warning while
  // its block failed to map, and under-labelling a short Leistung is the failure D2 exists
  // to prevent. ``unpricedNote`` is the block-grain sentence; this is its fallback.
  const shortfall =
    unpricedNote(block) ??
    (row.leistung_incomplete
      ? `${row.leistung_unpriced_posten} Posten konnten nicht bewertet werden und stehen mit 0,00 € drin.`
      : null);
  if (shortfall) out.push(shortfall);
  if (curatedPostenCount(block) > 0) out.push(CURATED_PRICE_NOTE);
  if (block.basis_unknown || row.leistung_basis_unknown) out.push(BASIS_UNKNOWN_NOTE);
  return out;
}


/**
 * #2476 — the reason token of a 0/blank calc-input line, as an organism row FLAG: amber
 * (``warning``) when inputs exist and only the price is missing (actionable), muted
 * (``neutral``) when nothing was recorded or when the zero is a rule's verdict (#2593 — the
 * trust tier behind the Anlagejahr rides in the hover). While the served records are still
 * loading, a busy placeholder holds the slot so the line never flashes "Keine Erfassung"
 * before "Preis fehlt" resolves.
 */
function reasonFlag(lineKey: string, reason: ZeroReason | 'pending'): MarginBreakdownFlag {
  if (reason === 'pending') return { label: '…', tone: 'neutral', title: 'Buchungen werden geladen' };
  if (reason.state === 'stand_outside_window' && reason.stand) {
    return { label: reasonTokenText(lineKey, reason), tone: 'neutral', title: standProvenanceNote(reason.stand.provenance) };
  }
  return { label: reasonTokenText(lineKey, reason), tone: reason.state === 'missing_price' ? 'warning' : 'neutral' };
}

/**
 * #2508 — the ``Rechenweg`` (derivation) table, rendered as the panel's formula iff the builder
 * emitted factors: factor name → value (+ links, ``geschätzt`` badge), closed by the ``= …`` row.
 */
function FactorsTable({
  factors,
  result,
  onNavigate,
}: {
  factors: BreakdownFactor[];
  result: BreakdownResult | null;
  onNavigate?: (to: string) => void;
}) {
  return (
    <table className="deckungsbeitrag-breakdown__table deckungsbeitrag-breakdown__table--factors">
      <caption className="deckungsbeitrag-breakdown__caption">Rechenweg</caption>
      <tbody>
        {factors.map((f) => (
          <tr key={f.key}>
            <th scope="row">{f.label}</th>
            <td>
              <FactorValue factor={f} onNavigate={onNavigate} />
            </td>
          </tr>
        ))}
      </tbody>
      {result && (
        <tfoot>
          <tr className="deckungsbeitrag-breakdown__total">
            <th scope="row">= {result.label}</th>
            <td className="deckungsbeitrag-breakdown__num">{result.value}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

/**
 * The selected line's Rechenweg for the organism's panel: the line's € (signed as displayed),
 * the derivation as the formula, the ``Einzelposten`` as the panel's lines (``Datum | Posten |
 * Betrag``, links inside Posten), and the reconciliation as the note (✓) or the warning.
 *
 * The operations path needs the lazy fetch; the estimate/allocations paths are fact-resident,
 * so a still-loading operations query never blanks them.
 */
function buildDetail(
  row: DeckungsbeitragRow,
  key: string,
  label: string,
  operations: OperationInfo[],
  isLoading: boolean,
  onNavigate?: (to: string) => void,
): MarginBreakdownDetailData | null {
  const breakdown = buildSourceBreakdown(row, key, operations);
  if (!breakdown) return null;
  const waiting = isLoading && breakdown.kind === 'operations' && breakdown.items.length === 0;
  // The Leistung's derivation is its Ertrag × Ø-Erlös line (once the muted line under the row
  // in the table, moved into the Rechenweg on 2026-09-07); an estimate brings its own factor
  // table instead, which already says „geschätzt".
  const formula =
    breakdown.factors.length > 0 ? (
      <FactorsTable factors={breakdown.factors} result={breakdown.result} onNavigate={onNavigate} />
    ) : key === 'leistung' ? (
      yieldLine(row)
    ) : undefined;
  const base = { title: `Aufschlüsselung: ${label}`, value: key === 'leistung' ? breakdown.lineValue : -breakdown.lineValue, formula };
  if (waiting) return { ...base, note: 'Buchungen werden geladen …' };
  if (breakdown.items.length === 0) {
    return {
      ...base,
      note: 'Keine Einzelbuchungen gefunden.',
      ...(worthReporting(breakdown.total, breakdown.lineValue)
        ? { warning: mismatchText(breakdown.total, breakdown.lineValue) }
        : {}),
    };
  }
  // HOF-510 — a breakdown that does not claim to BE the line gets NEITHER verdict. Printing
  // the ✓ would say two 0,00 € rows add up to a four-figure line; printing the mismatch would
  // accuse a document-derived line of being wrong because the rows beside it carry no price.
  // The note says what the rows actually are, which is the thing the farmer opened the line
  // to find out: these are the applications, and this is why they are not in the number.
  if (breakdown.derivesTheLine === false) {
    return {
      ...base,
      lines: breakdown.items.map((item) => ({
        period: item.date ? shortDay(item.date) : '—',
        text: <PostenCell item={item} onNavigate={onNavigate} />,
        amount: item.amount,
      })),
      note: UNPRICED_POSTEN_NOTE,
    };
  }
  return {
    ...base,
    lines: breakdown.items.map((item) => ({
      // A folded row (the Leistung months) names its month; a per-booking row its day.
      period: item.month ? formatMonth(item.month) : item.date ? shortDay(item.date) : '—',
      text: <PostenCell item={item} onNavigate={onNavigate} />,
      amount: item.amount,
    })),
    // HOF-512 — a matching Σ says NOTHING. The ✓ („Summe stimmt exakt mit der Zeile
    // überein") was reassurance for the normal case, printed under every reconciling line,
    // and a confirmation that fires almost always is one nobody reads: it cost a line of
    // the panel and trained the eye to skip the place where the mismatch appears. Silence
    // is the stronger signal — a note under the table now means something is off.
    ...(worthReporting(breakdown.total, breakdown.lineValue)
      ? { warning: mismatchText(breakdown.total, breakdown.lineValue) }
      : {}),
  };
}

/**
 * HOF-512 — whether a Σ that misses its line is worth telling the farmer about.
 *
 * ``reconciles`` is exact to the cent, and it has to stay that way: it is the predicate the
 * builders use to decide whether a breakdown may claim to DERIVE its line at all. But
 * printing every cent of disagreement put „einzelne Buchungen fehlen oder wurden anders
 * zugeordnet" under a 268.020,83 € vs. 268.020,84 € rounding artefact — a sentence that
 * accuses the data of being incomplete when what actually happened is that the worker
 * rounded each bucket to the cent before summing. A warning that cries wolf on a cent is
 * one a farmer learns to ignore on the day it means something.
 *
 * So the DISPLAY threshold is relative and coarse: report a gap of 5 % or more, stay quiet
 * below it. Relative because the tolerable absolute gap on a 268.020 € Leistung and on a
 * 12,00 € Sonstige line are not the same number, and 5 % because the model this drawer
 * serves is an 80–90 % model by construction (KB Part II §3, „the honest form of an 80–90 %
 * model") — a gap under a twentieth of the line is inside the accuracy the whole
 * calculation claims, and reporting it as a defect over-promises what the number is.
 *
 * A line of 0,00 € has no relative scale, so any non-trivial Σ against it is reported: that
 * is the „the line says nothing was spent, the bookings say otherwise" case, which is
 * exactly what the warning is for.
 */
const MISMATCH_REPORT_SHARE = 0.05;

function worthReporting(total: number, lineValue: number): boolean {
  const gap = Math.abs(total - lineValue);
  if (gap <= RECONCILE_TOLERANCE) return false;
  const scale = Math.abs(lineValue);
  if (scale <= RECONCILE_TOLERANCE) return true;
  return gap / scale >= MISMATCH_REPORT_SHARE;
}

/**
 * HOF-510 — the note under a breakdown whose rows are context rather than a derivation.
 *
 * It has to do two things at once: say that these applications carry no amount, and say that
 * the line above them is nonetheless right — because a farmer reading „kein Betrag" beside a
 * four-figure number will otherwise assume one of the two is broken.
 *
 * It says „kein Betrag ermittelt" and NOT „kein Preis hinterlegt" (codex r1, P2). A refusal is
 * not always a missing price: ``no_quantity``, ``no_article`` and ``unconvertible_unit`` are
 * refusals too, and a litre-vs-€/kg conversion gap told as „no price exists" sends the farmer
 * to correct the Preisspiegel for a defect that is not there. Each row states its own reason
 * on its ``postenNote``, so the summary names the effect and leaves the cause to the rows.
 */
const UNPRICED_POSTEN_NOTE =
  'Für diese Anwendungen konnte kein Betrag ermittelt werden — der Grund steht an der jeweiligen ' +
  'Zeile. Der Betrag der Zeile stammt aus den gebuchten Rechnungen und ist vollständig; die ' +
  'Anwendungen sind hier nur aufgeführt, damit erkennbar ist, welche Artikel betroffen sind.';

const mismatchText = (total: number, lineValue: number): string =>
  `Σ stimmt nicht exakt mit der Zeile überein (${formatEur(total)} vs. ${formatEur(lineValue)}) — ` +
  'einzelne Buchungen fehlen oder wurden anders zugeordnet.';

/**
 * #2773 C — the whole-row honesty chips, on the DB1 subtotal: the same ``chipsFor`` the ranked
 * table and the note below read, so the drawer and the overview can never disagree about why a
 * margin is not trusted. ``not_marketable`` is a verdict, not a warning → neutral tone. The
 * "ohne Gemeinkosten" caveat (#1645 R4) rides along as a neutral flag, because the DB1 the row
 * prints does not carry its shared-overhead share yet.
 */
function db1Flags(row: DeckungsbeitragRow): MarginBreakdownFlag[] {
  const flags: MarginBreakdownFlag[] = chipsFor(classifyRow(row), 'schlag').map((chip) => ({
    label: chip.label,
    tone: chip.reason === 'not_marketable' ? 'neutral' : 'warning',
    title: chip.title,
  }));
  if (row.incomplete_on_shared_overhead) {
    flags.push({ label: 'ohne Gemeinkosten', tone: 'neutral', title: 'Noch ohne Gemeinkosten gerechnet.' });
  }
  return flags;
}

/** The header meta line: Erntejahr, Fläche, Ertrag and the two geometry denominators the fact carries. */
function metaLine(row: DeckungsbeitragRow): string[] {
  const meta = [row.period ?? 'Ohne Jahr'];
  if (row.area_ha != null && row.area_ha > 0) meta.push(`${formatNumber(row.area_ha)}\u00a0ha`);
  if (row.yield > 0) meta.push(formatQty(row.yield, row.yield_unit));
  if (row.row_length_m != null && row.row_length_m > 0) meta.push(`${formatNumber(row.row_length_m)}\u00a0lfm`);
  if (row.plant_count != null && row.plant_count > 0) meta.push(`${formatNumber(row.plant_count)}\u00a0Pflanzen`);
  return meta;
}

/**
 * The Leistung's Rechenweg line: Ertrag and Ø-Erlös in the fact's OWN yield unit (#2168 —
 * dt for an arable harvest, kg for the berry pick stream; #2930 — through the same normaliser
 * the figures use). On an estimated row the Ø is neither realised nor an average — it is the
 * book price folded back through the yield — so the label says so (#2695 E). While revenue is
 * pending (#2168) the line is the neutral "noch keine Erlöse" note, never a 0,00 €/kg.
 * Until 2026-09-07 this was the muted line under the Leistung row in the table; the drawer
 * has its Ertrag in the meta line and its Ø in the je-kg basis, so the table lost the repeat
 * and the Rechenweg panel gained the derivation.
 */
function yieldLine(row: DeckungsbeitragRow): string | undefined {
  const eco = perBasisEconomics(row, 'kg');
  if (!eco) return undefined;
  if (eco.revenuePending || eco.revenuePerUnit == null) return REVENUE_PENDING_NOTE;
  const label = row.leistung_estimated ? 'Ø-Erlös (geschätzt)' : 'Ø-Erlös';
  return `Ertrag ${formatQty(eco.quantity, row.yield_unit)} · ${label} ${formatEurPerUnit(eco.revenuePerUnit, row.yield_unit)}`;
}

export default function RowDetail({
  row,
  basis = 'ha',
  selectedLine = null,
  onSelectLine,
  onNavigate,
  headerContainer = null,
}: {
  row: DeckungsbeitragRow;
  /** Where the organism renders its meta line and switchers — the drawer's title row (2026-09-07); null = in place. */
  headerContainer?: Element | null;
  /** The page's active Bezugsgröße (#2304) — the drawer's INITIAL basis; it switches on its own from there (HOF-467 AC 3). */
  basis?: MarginBasis;
  /** #2442 — the selected cascade-line bucket key (``?line=``); null = none selected. */
  selectedLine?: string | null;
  /** #2442 — toggle a line's selection (null = deselect). Absent → lines are inert. */
  onSelectLine?: (lineKey: string | null) => void;
  /** #2442 — SPA navigation for a breakdown deep-link (page wires useNavigate). */
  onNavigate?: (to: string) => void;
}) {
  // HOF-467 AC 3 — drawer-local: initialised from the page's ?basis=, then independent. The
  // panel remounts RowDetail per row (``key``), so a newly opened row starts from the page again.
  const [drawerBasis, setDrawerBasis] = useState<DrawerBasis>(basis);
  // Assumption 4 — a static, fail-closed crop map decides who gets the KTBL regrouping.
  const arable = isArableCrop(row.crop);
  // An arable cycle OPENS in the KTBL order — the one its advisors compare against, and the one
  // that does not lead a Winterraps with Pflücklohn, Verpackung and Pfandkisten. Schichten is one
  // click away. A berry cycle has no toggle and always reads by Schichten.
  const [view, setView] = useState<LayerView>(arable ? 'ktbl' : 'schichten');
  const activeView: LayerView = arable ? view : 'schichten';
  const basisOptions = useMemo(() => drawerBasisOptions(row), [row]);

  // Operations load lazily for the expanded cycle — shared by the harvest line, the #2476
  // reason flags AND the selected-line source breakdown (one React Query, deduped).
  const { operations, isLoading: operationsLoading } = useCropCycleOperations(row.crop_cycle_id);
  // #2574 — the served records THIS row may speak for. Empty on a #1720 thin per-year fact,
  // whose operations belong to another period (the fetch is per crop_cycle, not per period).
  const attributableOperations = operationsAttributable(row) ? operations : [];

  // #2476 — a 0/blank calc-input line explains itself. #2574 — every line whose reason
  // CONSULTS the served records waits on the lazy fetch (else it flashes "Keine Erfassung"
  // before "Preis fehlt" resolves); a thin per-year fact never consults them, so it never
  // waits. #2593 — nor does a stand verdict: it is carried by the fact itself.
  const zeroReason = (key: string): ZeroReason | 'pending' | null => {
    const missingInput = isMissingInputLine(row, key);
    if (!missingInput) return null;
    const standVerdict = key === 'saatgut' && zeroStateStand(row) !== null;
    const reasonPending = !standVerdict && operationsAttributable(row) && operationsLoading && operations.length === 0;
    return reasonPending ? 'pending' : bucketZeroReason(row, key, operations);
  };
  const decorate = (key: string): LayerDecoration => {
    const reason = zeroReason(key);
    const flag = reason ? reasonFlag(key, reason) : undefined;
    const selectability = lineSelectability(row, key, operations);
    if (selectability === 'selectable') return flag ? { flag } : {};
    if (selectability === 'disabled') return { flag, disabled: true, title: disabledTitle(row, key) };
    // static — a subtotal-like line, or a 0 with nothing behind it: inert, keeps its own hover
    return { flag, disabled: true };
  };
  const blocks = layerBlocks(row, activeView, { decorate: (line: CostLine) => decorate(line.key), db1Flags: db1Flags(row) });
  const leistungDeco = decorate('leistung');
  const leistung: MarginBreakdownLeistung = {
    id: 'leistung',
    // #2695 E — the label carries the warning, because it is the one piece of the table that is
    // on screen whether or not the notes below are read; the hover spells it out.
    label: row.leistung_estimated ? LEISTUNG_ESTIMATE_LABEL : 'Leistung',
    value: row.leistung,
    ...(leistungDeco.flag ? { flag: leistungDeco.flag } : {}),
    ...(leistungDeco.disabled ? { disabled: true } : {}),
    ...(leistungDeco.title ?? (row.leistung_estimated ? LEISTUNG_ESTIMATE_HOVER : undefined)
      ? { title: leistungDeco.title ?? LEISTUNG_ESTIMATE_HOVER }
      : {}),
  };

  // Only honour the selection when the addressed line is actually a source line for THIS row
  // (a stale ?line= from another Schlag reveals nothing). HOF-299 — a bookmarked
  // ``?line=duenger`` / ``?line=psm`` addresses the merged line. A pending layer row opens its
  // own sentence in the panel.
  const requestedLine = canonicalLineKey(selectedLine);
  // A pending layer row exists only in the Schichten view. In KTBL — where an arable cycle
  // opens — a bookmarked ``?line=pending:…`` or a selection carried across the toggle would
  // open a Rechenweg for a row the table does not show (codex, HOF-467 follow-up).
  const pendingSelected = activeView === 'schichten' ? pendingLine(requestedLine) : null;
  const selectableSelected =
    requestedLine != null && !pendingSelected && lineSelectability(row, requestedLine, operations) === 'selectable'
      ? requestedLine
      : null;
  const selectedRowId = pendingSelected ? pendingSelected.id : selectableSelected;
  const selectedLabel =
    selectableSelected === 'leistung'
      ? leistung.label
      : (blocks.flatMap((b) => b.rows).find((r) => r.id === selectableSelected)?.label ?? '');
  const detail: MarginBreakdownDetailData | null = pendingSelected
    ? { title: pendingSelected.label, value: null, pending: pendingSelected.text }
    : selectableSelected
      ? buildDetail(row, selectableSelected, selectedLabel, operations, operationsLoading, onNavigate)
      : null;

  // #2303/#2304 — the honesty line behind a derived Bezugsgröße denominator (row spacing /
  // plant density + source). The within-crop caveat is a property of the BASIS and is
  // therefore unconditional whenever a geometry basis is read (#2844).
  const geometryAssumption =
    drawerBasis !== 'total' && isGeometryBasis(drawerBasis)
      ? {
          text: geometryAssumptionText(row.geometry_provenance, drawerBasis),
          source: geometryAssumptionSource(row.geometry_provenance, drawerBasis),
        }
      : null;

  // HOF-512 — „Woher die Zahlen kommen" now rides in the RIGHT panel, under the Rechenweg.
  //
  // It was a full-width block under the table, which put the explanation of a number a
  // screen away from the number: on a tunnel cycle the farmer opened a line, read the Σ in
  // the panel, then scrolled past the whole cost table to find out what the line was made
  // of. Beside the panel it answers where it is asked. Two of its blocks are already
  // per-line (the basis note, and the Rechenweg above it), so the column now reads
  // top-to-bottom as one argument about the open line.
  //
  // It is NOT part of ``detail``: ``detail`` is null whenever no line is open, and the
  // provenance of the row does not stop being true because nothing is selected.
  const provenance = (
    <div className="deckungsbeitrag-detail__provenance">
      <h3 className="deckungsbeitrag-detail__heading">Woher die Zahlen kommen</h3>
      {/* codex r1 P1 — gated on the OPEN line, for the same reason the basis note below it
          is. „Leistung ist eine Schätzung" names one line; under an open Maschinen panel it
          is a caveat detached from the number it qualifies, which is the exact defect this
          change exists to remove. Still shown with nothing open, because then the panel is
          about the row as a whole and the disclosure must not be reachable only by clicking
          the line it is about. */}
      {row.leistung_estimated && (selectedRowId === null || selectedRowId === 'leistung') && (
        <LeistungEstimateNote estimate={row.leistung_estimate} />
      )}
      {/* The two are alternatives by construction — the worker emits ``leistung_estimate``
          and ``leistung_posten`` as an either/or — so this never stacks a second basis
          claim on top of the „geschätzt" one. */}
      {/* Layer C — first under the Rechenweg, so the euros charged to no cycle read as the
          continuation of the Belege table they were deliberately kept out of. */}
      {selectedRowId === INVESTITION_LINE && <InvestitionUnallocatedNote block={row.investition} />}
      <LineBasisNote row={row} lineKey={selectedRowId} />
      <RowQualityNote row={row} />
      {/* #4172 — a claim about the ROW (money the payroll carried that no line accounts
          for), not about the open line, so it is unconditional like RowQualityNote above
          rather than gated on the selection the way LeistungEstimateNote is. */}
      <LabourUnbookedNote unbooked={row.labour_unbooked} />
      <LabourUpliftGapNote gap={row.labour_uplift_gap} />
      <LabourShareNote row={row} lineKey={selectedRowId} />
      {/* A claim about the Verpackung line, so only under its open breakdown (2026-11-03). */}
      {selectedRowId === VERPACKUNG_LINE && <VerpackungUnpricedNote material={row.verpackung_material} />}
      {row.product_inconsistency && <ProductInconsistencyNote signal={row.product_inconsistency} />}
      <p>
        {row.source === 'operations'
          ? // codex r1 P1 — the SUBJECT is named. This is a claim about the whole
            // Deckungsbeitrag, and it always was; under the cost table that read as obvious
            // and inside a per-line panel it did not, so „Berechnet aus …" beside an open
            // Saatgut Rechenweg looked like a statement about the Saatgut number.
            `Dieser Deckungsbeitrag ist aus ${row.source_line_count} Einzelbuchung${
              row.source_line_count === 1 ? '' : 'en'
            } deiner Schlagkartei und Erntedaten berechnet.`
          : // #2574 — this paragraph may no longer claim the records do not EXIST. On a
            // money-less cycle the table above reads "Preis fehlt · N Buchungen", and the old
            // "noch ohne Einzelbuchungen" contradicted it on the same screen. The provenance
            // claim ("the € came from category sums") stays true either way; the record
            // sentence is ADDED once attributable records are served, so nothing false is
            // ever shown while the lazy fetch is still running.
            `Dieser Deckungsbeitrag ist aus Kategorien-Summen berechnet, nicht aus Einzelbuchungen.${
              attributableOperations.length > 0
                ? ` ${attributableOperations.length} erfasste Buchung${
                    attributableOperations.length === 1 ? '' : 'en'
                  } in der Schlagkartei ${
                    attributableOperations.length === 1 ? 'trägt' : 'tragen'
                  } keinen Preis.`
                : ''
            }`}
      </p>
      <HarvestLine operations={operations} />
    </div>
  );

  return (
    <div className="deckungsbeitrag-detail">
      <MarginBreakdown
        className="deckungsbeitrag-detail__breakdown"
        // No heading: the drawer title already names the row, and „So setzt sich das zusammen"
        // survives only as the lines table's accessible name (2026-09-07).
        headerContainer={headerContainer}
        meta={metaLine(row)}
        leistung={leistung}
        blocks={blocks}
        basisOptions={basisOptions}
        basis={drawerBasis}
        onBasisChange={(id) => {
          if (isDrawerBasis(id)) setDrawerBasis(id);
        }}
        {...(arable
          ? {
              viewOptions: LAYER_VIEW_OPTIONS,
              view: activeView,
              onViewChange: (id: string) => {
                if (id === 'schichten' || id === 'ktbl') setView(id);
              },
            }
          : {})}
        selectedRowId={selectedRowId}
        onSelectRow={onSelectLine ? (id) => onSelectLine(id) : undefined}
        detail={detail}
        emptyDetailText={SELECT_HINT}
        panelFooter={provenance}
      />
      {geometryAssumption && <GeometryAssumption text={geometryAssumption.text} source={geometryAssumption.source} />}
    </div>
  );
}
